<template>
  <div class="license-page">
    <div class="page-head">
      <div>
        <div class="page-title">功能权限</div>
        <div class="page-subtitle">维护服务端签名 payload 中允许出现的 feature_keys。</div>
      </div>
      <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">新增功能</el-button>
    </div>

    <div class="filter-bar">
      <el-input v-model="search.keyword" clearable placeholder="搜索 feature key / 功能名称" @change="readList">
        <template slot="prepend">关键词</template>
      </el-input>
      <el-select v-model="search.enabled" clearable placeholder="启用状态" @change="readList">
        <el-option label="启用" :value="1" />
        <el-option label="禁用" :value="0" />
      </el-select>
      <el-button icon="el-icon-refresh" @click="readList">刷新</el-button>
    </div>

    <el-table v-loading="loading" :data="list" border class="license-table">
      <el-table-column prop="feature_key" label="功能 key" min-width="190" />
      <el-table-column prop="feature_name" label="功能名称" min-width="150" />
      <el-table-column prop="category" label="分类" width="110" />
      <el-table-column prop="enforce_layer" label="控制层" width="110">
        <template slot-scope="scope">
          <el-tag size="mini" :type="layerTagType(scope.row.enforce_layer)">{{ scope.row.enforce_layer }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="security_level" label="安全等级" width="100">
        <template slot-scope="scope">
          <el-tag size="mini" :type="scope.row.security_level === 'hard' ? 'danger' : 'info'">{{ scope.row.security_level }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="enabled" label="状态" width="90">
        <template slot-scope="scope">
          <el-tag size="mini" :type="Number(scope.row.enabled) === 1 ? 'success' : 'info'">
            {{ Number(scope.row.enabled) === 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="sort" label="排序" width="80" />
      <el-table-column prop="description" label="说明" min-width="260" show-overflow-tooltip />
      <el-table-column label="操作" width="110" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" icon="el-icon-edit" @click="openEditDialog(scope.row)">编辑</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      :current-page="page_num"
      :page-size="page_size"
      :page-sizes="[10, 20, 50, 100]"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="820px" :append-to-body="true">
      <el-form :model="form" label-width="110px">
        <el-form-item label="分类">
          <el-select
            v-model="form.category"
            filterable
            allow-create
            default-first-option
            placeholder="先选择或创建分类，例如 audio"
            class="full-width"
            @change="handleCategoryChange"
          >
            <el-option
              v-for="item in categoryOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
              <span>{{ item.label }}</span>
              <span class="option-help">{{ item.value }}</span>
            </el-option>
          </el-select>
          <div class="form-help">分类是 feature key 的第一段，只允许小写字母、数字、下划线和短横线。</div>
        </el-form-item>
        <el-form-item label="动作名">
          <el-input v-model="form.key_name" placeholder="例如 play.speed" @input="handleKeyNameInput" />
          <div class="form-help">动作名会拼到分类后面，最终格式固定为“分类.动作名”。</div>
        </el-form-item>
        <el-form-item label="完整 key">
          <el-input :value="generatedFeatureKey" disabled />
          <div class="key-preview">保存到服务端的 feature_key：{{ generatedFeatureKey || '请先选择分类并填写动作名' }}</div>
        </el-form-item>
        <el-form-item label="功能名称">
          <el-input v-model="form.feature_name" placeholder="例如 变速播放" />
        </el-form-item>
        <el-form-item label="控制层">
          <el-radio-group v-model="form.enforce_layer" class="choice-grid">
            <el-radio label="dll" border>
              <span class="choice-title">DLL</span>
              <span class="choice-desc">原生执行点强校验，适合播放、录音、导入导出等关键动作。</span>
            </el-radio>
            <el-radio label="main" border>
              <span class="choice-title">Main</span>
              <span class="choice-desc">Electron 主进程收口，适合窗口打开、快捷键分发等流程控制。</span>
            </el-radio>
            <el-radio label="server" border>
              <span class="choice-title">Server</span>
              <span class="choice-desc">服务端强校验，适合云端浏览、试听地址、下载接口。</span>
            </el-radio>
            <el-radio label="renderer" border>
              <span class="choice-title">Renderer</span>
              <span class="choice-desc">只用于界面展示或提示，不作为安全边界。</span>
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="安全等级">
          <el-radio-group v-model="form.security_level" class="choice-grid two-cols">
            <el-radio label="hard" border>
              <span class="choice-title">Hard</span>
              <span class="choice-desc">必须强制拦截；无权限时不能继续执行或 fallback。</span>
            </el-radio>
            <el-radio label="soft" border>
              <span class="choice-title">Soft</span>
              <span class="choice-desc">只影响展示、入口提示或运营引导，不承载安全防线。</span>
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="启用状态">
          <el-switch v-model="form.enabled" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort" :min="0" :max="999999" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="form.description" type="textarea" :autosize="{ minRows: 3, maxRows: 5 }" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveFeature">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import apis from '@/api/index'

export default {
  name: 'LicenseFeatures',
  data() {
    return {
      loading: false,
      saving: false,
      dialogVisible: false,
      dialogTitle: '新增功能',
      page_num: 1,
      page_size: 20,
      total: 0,
      search: {
        keyword: '',
        enabled: '',
      },
      list: [],
      form: this.createEmptyForm(),
    }
  },
  computed: {
    // 当前页面可选分类由默认分类和列表中已有分类合并而来，避免操作员重复输入相近分类。
    categoryOptions() {
      const defaults = [
        { value: 'audio', label: '音频 audio' },
        { value: 'record', label: '录音 record' },
        { value: 'ducking', label: '闪避压音 ducking' },
        { value: 'zip', label: '音效包 zip' },
        { value: 'cloud', label: '云端 cloud' },
        { value: 'clip', label: '剪辑 clip' },
        { value: 'media', label: '媒体 media' },
        { value: 'hotkey', label: '快捷键 hotkey' },
        { value: 'prompter', label: '提词器 prompter' },
      ]
      const map = {}
      defaults.forEach((item) => {
        map[item.value] = item
      })
      this.list.forEach((row) => {
        const value = this.normalizeCategory(row.category || this.getCategoryFromFeatureKey(row.feature_key))
        if (value && !map[value]) {
          map[value] = { value, label: value }
        }
      })
      return Object.keys(map).sort().map((key) => map[key])
    },
    // 完整 feature_key 永远由“分类.动作名”生成，保存前再写回 form.feature_key。
    generatedFeatureKey() {
      const category = this.normalizeCategory(this.form.category)
      const keyName = this.normalizeKeyName(this.form.key_name)
      if (!category || !keyName) return ''
      return category + '.' + keyName
    },
  },
  methods: {
    // 创建空表单，避免新增和编辑共用引用导致字段残留。
    createEmptyForm() {
      return {
        id: 0,
        feature_key: '',
        key_name: '',
        feature_name: '',
        category: '',
        enforce_layer: 'dll',
        security_level: 'hard',
        enabled: 1,
        sort: 0,
        description: '',
      }
    },
    // 分类只允许作为 feature_key 第一段，统一转小写并去掉不合法字符。
    normalizeCategory(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '')
    },
    // 动作名允许多段，例如 play.speed；连续点会被合并，首尾点会被去掉。
    normalizeKeyName(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9._-]/g, '')
        .replace(/\.+/g, '.')
        .replace(/^\./, '')
        .replace(/\.$/, '')
    },
    // 从完整 feature_key 中取分类，旧数据也可以被新表单正常编辑。
    getCategoryFromFeatureKey(featureKey) {
      const key = String(featureKey || '')
      const index = key.indexOf('.')
      return index > 0 ? key.slice(0, index) : ''
    },
    // 从完整 feature_key 中取动作名，保证编辑旧数据时不会让操作员重新拆 key。
    getKeyNameFromFeatureKey(featureKey, category) {
      const key = String(featureKey || '')
      const prefix = String(category || '') + '.'
      return key.indexOf(prefix) === 0 ? key.slice(prefix.length) : ''
    },
    // 分类变更时立即规范化，防止中文空格或大写字母进入最终 key。
    handleCategoryChange(value) {
      this.form.category = this.normalizeCategory(value)
    },
    // 动作名输入时立即规范化，最终 key 预览同步更新。
    handleKeyNameInput(value) {
      const normalized = this.normalizeKeyName(value)
      if (normalized !== value) {
        this.form.key_name = normalized
      }
    },
    // 根据控制层返回标签颜色，让列表扫描时能快速识别强控制点。
    layerTagType(layer) {
      if (layer === 'dll') return 'danger'
      if (layer === 'server') return 'warning'
      if (layer === 'main') return 'success'
      return 'info'
    },
    // 读取功能权限列表，所有筛选条件都在服务端收口。
    readList(resetPage = true) {
      if (resetPage) this.page_num = 1
      this.loading = true
      const params = Object.assign({}, this.search, {
        page_num: this.page_num,
        page_size: this.page_size,
      })
      apis.license.readFeatures(params).then((res) => {
        const data = res.data && res.data.data ? res.data.data : {}
        this.list = data.list || []
        this.total = Number(data.count || 0)
      }).catch((err) => {
        this.$message({ message: String(err || '读取功能权限失败'), type: 'error' })
      }).finally(() => {
        this.loading = false
      })
    },
    // 打开新增弹窗，显式重置表单。
    openCreateDialog() {
      this.dialogTitle = '新增功能'
      this.form = this.createEmptyForm()
      this.dialogVisible = true
    },
    // 打开编辑弹窗，复制行数据，避免表格行在未保存时被直接修改。
    openEditDialog(row) {
      this.dialogTitle = '编辑功能'
      this.form = Object.assign(this.createEmptyForm(), row)
      this.form.enabled = Number(this.form.enabled || 0) > 0 ? 1 : 0
      this.form.sort = Number(this.form.sort || 0)
      this.form.category = this.normalizeCategory(this.form.category || this.getCategoryFromFeatureKey(this.form.feature_key))
      this.form.key_name = this.normalizeKeyName(this.getKeyNameFromFeatureKey(this.form.feature_key, this.form.category))
      this.dialogVisible = true
    },
    // 保存功能权限，失败时保留弹窗，便于修正后继续提交。
    saveFeature() {
      const featureKey = this.generatedFeatureKey
      if (!featureKey) {
        this.$message({ message: '请先选择分类并填写动作名', type: 'warning' })
        return
      }
      this.form.feature_key = featureKey
      this.form.category = this.normalizeCategory(this.form.category)
      this.saving = true
      apis.license.saveFeature(this.form).then((res) => {
        const body = res.data || {}
        if (body.code !== 1) {
          this.$message({ message: body.msg || '保存失败', type: 'error' })
          return
        }
        this.$message({ message: '保存成功', type: 'success' })
        this.dialogVisible = false
        this.readList(false)
      }).catch((err) => {
        this.$message({ message: String(err || '保存失败'), type: 'error' })
      }).finally(() => {
        this.saving = false
      })
    },
    // 每页数量变化时保持当前筛选条件，只重置到第一页。
    handleSizeChange(size) {
      this.page_size = size
      this.readList(true)
    },
    // 页码变化时保持当前筛选条件。
    handleCurrentChange(page) {
      this.page_num = page
      this.readList(false)
    },
  },
  mounted() {
    this.readList()
  },
}
</script>

<style lang="scss" scoped>
.license-page {
  padding: 18px;
  background: #f5f7fa;
  min-height: calc(100vh - 36px);
}
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.page-title {
  font-size: 24px;
  color: #1f2d3d;
  line-height: 32px;
}
.page-subtitle {
  color: #6b7280;
  font-size: 13px;
  margin-top: 4px;
}
.filter-bar {
  display: grid;
  grid-template-columns: minmax(280px, 420px) 160px 96px;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
}
.license-table {
  background: #fff;
}
.pager {
  margin-top: 14px;
  text-align: right;
}
.full-width {
  width: 100%;
}
.option-help {
  float: right;
  color: #909399;
  font-size: 12px;
}
.form-help,
.key-preview {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}
.key-preview {
  color: #606266;
  font-family: Consolas, Monaco, monospace;
}
.choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;

  ::v-deep .el-radio.is-bordered {
    height: auto;
    min-height: 76px;
    margin: 0;
    padding: 12px 14px;
    white-space: normal;
    line-height: 20px;
  }

  ::v-deep .el-radio__label {
    display: inline-flex;
    flex-direction: column;
    gap: 3px;
    vertical-align: top;
  }
}
.choice-grid.two-cols {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.choice-title {
  color: #303133;
  font-weight: 600;
}
.choice-desc {
  color: #6b7280;
  font-size: 12px;
  line-height: 18px;
}
</style>
